import { Link } from 'react-router-dom';
import type { Invoice } from '@faktorplus/shared';
import { useInvoiceList } from '../../api/invoices';
import { useCurrentUser } from '../../auth/auth-provider';
import { EmptyState, ErrorBox, LoadingBlock } from '../../components/ui/feedback';
import { MissingProtocolFlag } from '../../components/ui/status-pill';
import { ClockAlertIcon } from '../../components/ui/icons';
import { formatAmount, formatDayCount, formatMoney, formatShortDate } from '../../lib/format';
import pageStyles from '../../components/layout/page.module.css';
import styles from './manager-queue-page.module.css';

/** Days before the waiting counter turns red, matching the reminder threshold. */
const LATE_AFTER_DAYS = 5;

/**
 * The site manager's home screen: a stack of cards to work through, not a table.
 *
 * Everything needed to recognise an invoice fits in one glance — who, how much,
 * which site, how long it has been waiting.
 */
export function ManagerQueuePage() {
  const user = useCurrentUser();
  // Only invoices booked to this manager. Reading is scoped per site, but
  // approving requires being the named manager — listing a colleague's invoice
  // under "Czekają na Ciebie" would offer a button the API answers 403 to.
  const { data, isLoading, error } = useInvoiceList({
    status: 'ASSIGNED',
    assignedToMe: true,
    sortBy: 'receivedDate',
    sortDir: 'asc',
    pageSize: 50,
  });

  if (isLoading) return <LoadingBlock />;
  if (error) return <ErrorBox>Nie udało się pobrać faktur. Odśwież stronę.</ErrorBox>;

  const invoices = data?.items ?? [];

  return (
    <>
      <header className={pageStyles.header}>
        <h1 className={pageStyles.title}>Czekają na Ciebie</h1>
        <span className={pageStyles.subtitle}>
          {invoices.length > 0
            ? `${invoices.length} ${pluraliseInvoices(invoices.length)} · ${user.fullName}`
            : user.fullName}
        </span>
      </header>

      {invoices.length === 0 ? (
        <EmptyState
          title="Nic nie czeka"
          description="Wszystkie faktury z Twoich budów są zaakceptowane."
        />
      ) : (
        <div className={styles.list}>
          {invoices.map((invoice) => (
            <InvoiceCard key={invoice.id} invoice={invoice} />
          ))}
        </div>
      )}
    </>
  );
}

function InvoiceCard({ invoice }: { invoice: Invoice }) {
  const waitingDays = invoice.daysAwaitingManager ?? 0;
  const isLate = waitingDays >= LATE_AFTER_DAYS;
  const project = invoice.allocations[0]?.projectName;
  const needsProtocol = invoice.requiresHandoverProtocol && !invoice.documentsCompleteAt;

  return (
    <Link to={`/faktury/${invoice.id}`} className={styles.card}>
      <div className={styles.main}>
        <div className={styles.top}>
          <span className={styles.vendor}>{invoice.vendorName}</span>
          {needsProtocol && <MissingProtocolFlag />}
        </div>

        <div className={styles.meta}>
          <span className={styles.project}>{project ?? 'Koszt ogólny'}</span>
          <span className={styles.docNumber}>{invoice.number}</span>
          <span className={styles.metaItem}>{invoice.categoryName ?? 'bez kategorii'}</span>
          <span className={styles.metaItem}>wyst. {formatShortDate(invoice.issueDate)}</span>
        </div>
      </div>

      <div className={styles.amounts}>
        <span className={styles.amount}>{formatMoney(invoice.grossAmount)}</span>
        <span className={styles.amountSub}>
          netto {formatAmount(invoice.netAmount)} · VAT {formatAmount(invoice.vatAmount)}
        </span>
      </div>

      <div className={styles.side}>
        <span className={`${styles.age} ${isLate ? styles.ageLate : ''}`}>
          <ClockAlertIcon className={styles.ageIcon} />
          {waitingDays === 0 ? 'dziś' : formatDayCount(waitingDays)}
        </span>
        <span className={styles.chevron} aria-hidden="true">
          →
        </span>
      </div>
    </Link>
  );
}

function pluraliseInvoices(count: number): string {
  if (count === 1) return 'faktura';
  const lastDigit = count % 10;
  const lastTwo = count % 100;
  const isFew = lastDigit >= 2 && lastDigit <= 4 && (lastTwo < 12 || lastTwo > 14);
  return isFew ? 'faktury' : 'faktur';
}
